import React, { useState, useEffect } from 'react';
import { NamedCollection } from '../types';
import { slideApiService } from '../services/slideApiService';
import { useShop } from '../contexts/ShopContext';
import { collectionCreatorLabel, collectionPermissions } from '../utils/collectionPermissions';

/**
 * Scrollable picker for adding the current favorites set to an existing collection.
 * Mirrors the iOS AddToCollectionSheet. Does NOT clear favorites — the same set can
 * be added to multiple collections. Selecting a collection calls onPick(collection).
 * Lists only collections the user may edit: their own, plus shop-visible ones when their role
 * allows editing (server-computed permissions).
 */
interface AddToCollectionModalProps {
  shopId: string;
  count: number; // number of favorites being added (for the header)
  onPick: (collection: NamedCollection) => void;
  onClose: () => void;
}

const BORDER = 'var(--border-light)';
const MUTED = 'var(--text-accent)';
const TEXT = 'var(--text-accent)';
const PRIMARY = 'var(--text-accent)';

const AddToCollectionModal: React.FC<AddToCollectionModalProps> = ({ shopId, count, onPick, onClose }) => {
  const { userRole } = useShop();
  const [collections, setCollections] = useState<NamedCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await slideApiService.listCollections(shopId);
        if (!cancelled) setCollections(result.filter(c => collectionPermissions(c, userRole).canEdit));
      } catch (err) {
        console.error('Failed to load collections:', err);
        if (!cancelled) setError('Failed to load collections.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, userRole]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '460px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: '1.125rem', fontWeight: 600, color: TEXT }}>Add to Collection</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: MUTED }}>
          Add {count} favorited {count === 1 ? 'slide' : 'slides'} to an existing collection.
        </p>

        {error && <div className="text-error" style={{ marginBottom: '12px' }}>{error}</div>}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: MUTED }}>
              <div className="spinner-sm mx-auto mb-2" />
              Loading…
            </div>
          ) : collections.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: MUTED, fontSize: '0.875rem' }}>
              No collections yet. Use "Save as New Collection" first.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {collections.map(collection => {
                const creator = collectionCreatorLabel(collection);
                return (
                <button
                  key={collection.id}
                  onClick={() => onPick(collection)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', textAlign: 'left', padding: '12px 14px',
                    border: `1px solid ${BORDER}`, borderRadius: '8px', backgroundColor: 'var(--bg-card)', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = PRIMARY; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600, color: TEXT, display: 'block' }}>{collection.name}</span>
                    {creator && (
                      <span style={{ fontSize: '0.75rem', color: MUTED }}>by {creator}</span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.8125rem', color: MUTED, whiteSpace: 'nowrap', marginLeft: '12px' }}>
                    {collection.slideCount} {collection.slideCount === 1 ? 'slide' : 'slides'}
                  </span>
                </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default AddToCollectionModal;
